import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import { ProfileLoginFormInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-login-form.dto';
import { UserEmailDto, type UserEmailInterface } from '@zaoblako/shared/modules/security/dtos/user/user-email.dto';
import { UserLoginDto } from '@zaoblako/shared/modules/security/dtos/user/user-login.dto';
import { AppConfigFindInterface } from '@zaoblako/shared/modules/system/dtos/app-config-find.dto';
import { BasisSystemAppMetadataService } from '@zaoblako/web/core/services/basis/basis-system-app-metadata.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { RoutingService } from '@zaoblako/web/core/services/routing.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { TenantService } from '@zaoblako/web/core/services/security/tenant.service';
import { ValidationService } from '@zaoblako/web/core/services/validation/validation.service';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import * as moment from 'moment-timezone';
import { SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MessagesModule } from 'primeng/messages';

@Component({
  standalone: true,
  selector: 'crm-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    CheckboxModule,
    ButtonModule,
    MessagesModule,
    SharedModule,
    InputTextComponent,
    JoinPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {
  public loading = false;
  public isReset = false;

  public isShowReg = false;

  public config: AppConfigFindInterface;

  public login: ProfileLoginFormInterface = {
    email: '',
    password: '',
    remember: false
  };

  public terms: { is: boolean; error: string | undefined } = {
    is: true,
    error: undefined
  };

  public errorsLogin: ValidationErrorInterface<ProfileLoginFormInterface>;

  public resetEmail: UserEmailInterface = { email: '' };
  public errorsEmail: ValidationErrorInterface<UserEmailInterface>;
  public isSendResetEmail = false;

  public errBack: any;
  public footerYear: string;
  private url: string;
  private queryParams: any;
  private isNewsletter = false;

  public constructor(
    private readonly authService: AuthService,
    private readonly profileService: ProfileService,
    private readonly tenantService: TenantService,
    private readonly route: ActivatedRoute,
    private readonly routingService: RoutingService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly validationService: ValidationService,
    private readonly basisSystemAppMetadataService: BasisSystemAppMetadataService
  ) {}

  public ngOnInit() {
    this.isNewsletter = this.route.snapshot.queryParams?.isNewsletter === 'true';
    const justRegistered = this.route.snapshot.queryParams?.justRegistered === 'true';

    if (justRegistered) {
      this.errorService.addSuccessMessage('Вам на почту выслали логин и пароль от системы');
    }

    this.url = this.route.snapshot.queryParams['url'] || sessionStorage.getItem('redirectUrlInAuth') || '/';
    this.queryParams = this.url.split('?')[1] || sessionStorage.getItem('redirectQueryParamAuth') || '';

    this.url = this.url.split('?')[0] || this.url;
    this.queryParams = this.queryParams
      ? JSON.parse('{"' + decodeURI(this.queryParams.replace(/&/g, '","').replace(/=/g, '":"')) + '"}')
      : {};

    if (moment().year() === 2022) {
      this.footerYear = '2022';
    } else {
      this.footerYear = `2022-${moment().year()}`;
    }

    if (this.authService.currentProfile) {
      if (this.url === '/') {
        this.routingService.goToPreviousUrl(this.authService.currentProfile?.sessionMode);
      } else {
        sessionStorage.removeItem('redirectUrlInAuth');
        sessionStorage.removeItem('redirectQueryParamAuth');
        this.router.navigate([this.url], { queryParams: this.queryParams }).catch(console.error);
      }
    }
    this.cdRef.markForCheck();

    this.basisSystemAppMetadataService.getConfig().subscribe({
      next: (config) => {
        this.config = config;
        this.cdRef.markForCheck();
      }
    });

    this.tenantService.getSubdomains().subscribe({
      next: (data) => {
        if ([data.MAIN, data.ALIAS].includes(location.hostname)) {
          this.isShowReg = true;
          this.cdRef.markForCheck();
        }
      }
    });
  }

  public loginClick() {
    this.loading = true;
    this.cdRef.markForCheck();

    if (!this.terms.is) {
      this.loading = false;
      this.terms.error = 'Необходимо принять соглашение';
      this.cdRef.markForCheck();
      return;
    }

    this.terms.error = undefined;
    this.login.password = this.login.password?.trim();

    const [body, errors] = this.validationService.parseAndValidate(this.login, UserLoginDto);
    if (!body) {
      this.errorsLogin = errors?.validationErrors;
      console.log(this.errorsLogin);
      this.loading = false;
      this.cdRef.markForCheck();
    } else {
      this.errorsLogin = null;
      this.cdRef.markForCheck();

      this.authService.login(body).subscribe({
        next: (user) => {
          this.loading = false;
          this.cdRef.markForCheck();
          if (this.isNewsletter) {
            return this.router.navigate(['debts/newsletter']).catch(console.error);
          }
          if (this.url === '/') {
            this.routingService.goToPreviousUrl(user.sessionMode);
          } else {
            sessionStorage.removeItem('redirectUrlInAuth');
            sessionStorage.removeItem('redirectQueryParamAuth');
            this.router.navigate([this.url], { queryParams: this.queryParams }).catch(console.error);
          }
        },
        error: (err) => {
          this.loading = false;
          this.errBack = err;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public resetClick() {
    this.loading = true;
    this.cdRef.markForCheck();

    const [body, errors] = this.validationService.parseAndValidate(this.resetEmail, UserEmailDto);
    if (!body) {
      this.errorsEmail = errors?.validationErrors;
      console.log(this.errorsEmail);
      this.loading = false;
      this.cdRef.markForCheck();
    } else {
      this.errorsEmail = null;
      this.cdRef.markForCheck();

      this.profileService.resetPassword(body).subscribe({
        next: () => {
          this.loading = false;
          this.resetEmail.email = '';
          this.isSendResetEmail = true;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public change() {
    this.isReset = !this.isReset;
    this.isSendResetEmail = false;
    if (this.isReset && this.login.email && !this.resetEmail.email) {
      this.resetEmail.email = this.login.email;
    }
  }
}
